import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { albumService } from "../services/albumService";
import { AlbumComment, CreateAlbumCommentDto, UpdateAlbumCommentDto } from "../types/album";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import { authService } from "../services/authService";

export const albumCommentKeys = {
  all: ['albumComments'] as const,
  lists: () => [...albumCommentKeys.all, 'list'] as const,
  list: (ownerId: string, albumId: string) => [...albumCommentKeys.lists(), ownerId, albumId] as const,
};

export const useAlbumComments = (ownerId?: string, albumId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: comments = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: albumCommentKeys.list(ownerId!, albumId!),
    queryFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.getAlbumComments(ownerId, albumId);
    },
    enabled: !!ownerId && !!albumId,
  });

  const createCommentMutation = useMutation({
    mutationFn: async (commentData: CreateAlbumCommentDto) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.createAlbumComment(ownerId, albumId, commentData);
    },
    onSuccess: (newComment) => {
      queryClient.setQueryData<AlbumComment[]>(albumCommentKeys.list(ownerId!, albumId!), (old) => {
        return old ? [...old, newComment] : [newComment];
      });
    },
    onError: (error) => {
      console.error("Error creating comment:", handleApiError(error));
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, updateData }: { commentId: string; updateData: UpdateAlbumCommentDto }) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.updateAlbumComment(ownerId, albumId, commentId, updateData);
    },
    onSuccess: (updatedComment) => {
      queryClient.setQueryData<AlbumComment[]>(albumCommentKeys.list(ownerId!, albumId!), (old) => {
        return old ? old.map((comment) => (comment.id === updatedComment.id ? updatedComment : comment)) : [updatedComment];
      });
    },
    onError: (error) => {
      console.error("Error updating comment:", handleApiError(error));
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.deleteAlbumComment(ownerId, albumId, commentId);
    },
    onSuccess: (_, commentId) => {
      queryClient.setQueryData<AlbumComment[]>(albumCommentKeys.list(ownerId!, albumId!), (old) => {
        return old ? old.filter((comment) => comment.id !== commentId) : [];
      });
    },
    onError: (error) => {
      console.error("Error deleting comment:", handleApiError(error));
    },
  });

  return {
    // Data
    comments,
    hasComments: comments.length > 0,

    // Loading states
    isLoading,
    isCreating: createCommentMutation.isPending,
    isUpdating: updateCommentMutation.isPending,
    isDeleting: deleteCommentMutation.isPending,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : null,

    // Actions
    createComment: createCommentMutation.mutateAsync,
    updateComment: (commentId: string, updateData: UpdateAlbumCommentDto) =>
      updateCommentMutation.mutateAsync({ commentId, updateData }),
    deleteComment: deleteCommentMutation.mutateAsync,
    refreshComments: refetch,
    clearError: () => {
      queryClient.resetQueries({ queryKey: albumCommentKeys.list(ownerId!, albumId!) });
    },

    // Query client for manual invalidation
    invalidateComments: () => queryClient.invalidateQueries({ queryKey: albumCommentKeys.list(ownerId!, albumId!) }),
  };
};